import * as BABYLON from 'babylonjs';
import 'babylonjs-loaders';

import { WorldModel, ToolType } from './models/WorldModel';
import { WorldView } from './views/WorldView';
import { WorldController } from './controllers/WorldController';
import { InputService } from './services/InputService';
import { ConsoleView } from './views/ConsoleView';
import { ConsoleController } from './controllers/ConsoleController';
import { AudioService } from './services/AudioService';
import { UIManager } from './controllers/UIManager';
import { PubSub } from './PubSub';
import type { GameEvents } from './events';
import { Level1 } from './levels/Level1';
import { Level2 } from './levels/Level2';
import { LEVEL2 } from './models/constants';
import type { IGameLevel } from './levels/IGameLevel';
import { SocialCohesionService } from './services/SocialCohesionService';
import { gameOver } from './assets/text';

// --- Configuration ---
const CONFIG = {
    TERRAIN_SIZE: 100,
    TERRAIN_SUBDIVISIONS: 100,
};

/**
 * Top-level game class. Owns the Babylon engine, scene, camera, and MVC world objects.
 * Instantiated once on page load.
 */
class Game {
    private canvas: HTMLCanvasElement;
    private engine: BABYLON.Engine;
    private scene: BABYLON.Scene;
    private camera!: BABYLON.ArcRotateCamera;
    private worldModel!: WorldModel;
    private worldView!: WorldView;
    private worldController!: WorldController;
    private uiManager!: UIManager;

    /**
     * @param canvasId - The `id` of the `<canvas>` element to render into.
     */
    constructor(canvasId: string) {
        this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        this.engine = new BABYLON.Engine(this.canvas, true);
        this.scene = this.createScene();

        // Handle window resize
        window.addEventListener('resize', () => {
            this.engine.resize();
        });

        setInterval(() => {
            this.worldController.tick();
            this.uiManager.updateBeingStats();
        }, 500);

        // Start render loop
        this.engine.runRenderLoop(() => {
            this.scene.render();
        });
    }

    /**
     * Reads the `?level={number}` query parameter. Returns the requested level
     * number when present and valid (a positive integer), or `null` when the
     * parameter is absent or malformed. A non-null result signals the caller to
     * start at that level and skip the console intro.
     */
    private getRequestedLevel(): number | null {
        const param = new URLSearchParams(window.location.search).get('level');
        if (param === null) {
            return null;
        }
        const level = Number.parseInt(param, 10);
        return (Number.isInteger(level) && level >= 1) ? level : null;
    }

    /**
     * Builds and returns the Babylon scene: camera, lighting, shadow generator,
     * MVC world objects, and toolbar button wiring.
     */
    private createScene(): BABYLON.Scene {
        const scene = new BABYLON.Scene(this.engine);
        scene.clearColor = new BABYLON.Color4(0.3, 0.6, 1.0, 1.0);

        this.camera = new BABYLON.ArcRotateCamera(
            "Camera",
            -Math.PI / 4, // North-West
            Math.PI / 3,  // 30 degrees from the ground (60 from vertical)
            100,
            BABYLON.Vector3.Zero(),
            scene
        );
        this.camera.attachControl(this.canvas, true);

        // Lock orientation: Remove rotation but keep panning and zoom
        this.camera.panningSensibility = 100;
        this.camera.panningAxis = new BABYLON.Vector3(1, 0, 1); // Force movement on XZ plane only
        this.camera.angularSensibilityX = Infinity;
        this.camera.angularSensibilityY = Infinity;

        // Force strictly horizontal movement by locking the target's Y
        const initialY = this.camera.target.y;
        scene.onBeforeRenderObservable.add(() => {
            if (this.camera.target.y !== initialY) {
                this.camera.target.y = initialY;
            }
        });

        // Hemisphere provides a cool sky-bounce from above and a warm ground-bounce
        // from below, giving the world a subtle two-tone ambient that complements
        // the directional sun without washing out the flat-shaded facets.
        const hemiLight = new BABYLON.HemisphericLight("hemi", new BABYLON.Vector3(0, 1, 0), scene);
        hemiLight.intensity = 0.35;
        hemiLight.diffuse = new BABYLON.Color3(0.85, 0.92, 1.0);   // cool sky tint
        hemiLight.groundColor = new BABYLON.Color3(0.4, 0.3, 0.15); // warm ground bounce

        // Warm directional sun — yellow-orange cast per the Style Guide ("warm, directional").
        const dirLight = new BABYLON.DirectionalLight("sun", new BABYLON.Vector3(-1, -2, -1), scene);
        dirLight.position = new BABYLON.Vector3(20, 40, 20);
        dirLight.intensity = 1.0;
        dirLight.diffuse = new BABYLON.Color3(1.0, 0.88, 0.65);   // warm golden sun
        dirLight.specular = new BABYLON.Color3(0.0, 0.0, 0.0);    // no specular — toy materials are matte

        const shadowGenerator = new BABYLON.ShadowGenerator(1024, dirLight);
        shadowGenerator.useExponentialShadowMap = true;
        shadowGenerator.transparencyShadow = true;

        const pubsub = new PubSub<GameEvents>();
        AudioService.getInstance().subscribeToEvents(pubsub);

        new ConsoleView(pubsub);
        const consoleController = new ConsoleController(pubsub);

        const cohesionService = new SocialCohesionService();
        const level1 = new Level1({ consoleController, pubsub, cohesionService });

        this.worldModel = new WorldModel(CONFIG.TERRAIN_SIZE, CONFIG.TERRAIN_SUBDIVISIONS, level1);
        this.worldView = new WorldView(scene, this.worldModel, shadowGenerator, pubsub);
        this.worldController = new WorldController(this.worldModel, pubsub);
        this.worldController.generateTerrain();
        new InputService(scene, this.canvas, this.camera, this.worldModel, pubsub, this.worldView.getGroundMesh(), this.worldController);

        // A `?level={number}` query param starts the game at that level and skips
        // the console intro. Absent or invalid params fall back to Level 1 with its intro.
        const requestedLevel = this.getRequestedLevel();

        let startLevel: IGameLevel = level1;
        if (requestedLevel === 2) {
            startLevel = new Level2({ consoleController, pubsub, worldController: this.worldController });
            this.worldModel.level = startLevel;
            pubsub.publish('world:spawn-beings', 10);
            // This path deliberately skips `begin()` (and with it the intro console
            // sequence), so the beasts it would have scattered are spawned here.
            this.worldController.spawnBeasts(LEVEL2.BEAST_COUNT);
        }

        if (requestedLevel === null) {
            startLevel.begin();
        }
        pubsub.publish('level:started', startLevel.id);

        pubsub.subscribe('level:complete', (levelId: string) => {
            if (levelId === 'level-1') {
                const level2 = new Level2({ consoleController, pubsub, worldController: this.worldController });
                this.worldModel.level = level2;
                pubsub.publish('level:started', level2.id);
                // Wait for the Level 1 completion text (already playing) to be dismissed
                // before showing Level 2's intro, so the two scripts don't overwrite each other.
                consoleController.whenIdle().then(() => level2.begin());
            }
        });

        pubsub.subscribe('game:over', () => consoleController.play(gameOver));
        pubsub.subscribe('game:restart', () => location.reload());

        this.uiManager = new UIManager(this.worldModel, pubsub);

        return scene;
    }
}

// Start the game
new Game('renderCanvas');
